/**
 * Client-side audio slicing for reference-audio uploads. When the user
 * picks a full-length song from their library, we let them scrub to a
 * 60-second window and only ship THAT slice as the reference — Mureka
 * expects a short seed clip, not a 7-minute track, and the JSON+base64
 * upload path caps around ~10MB anyway.
 *
 * Everything here is pure Web Audio API — no wasm codecs, no bundled
 * ffmpeg. We decode via AudioContext, hand back a Float32 peak envelope
 * for the waveform draw, and encode the extracted slice as PCM WAV
 * (uncompressed, but ~60s at 44.1k mono is ~5MB, well within budget).
 */

let sharedCtx: AudioContext | null = null;

function getCtx(): AudioContext {
  if (!sharedCtx) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    sharedCtx = new AC();
  }
  return sharedCtx;
}

/**
 * Decode an audio File to an AudioBuffer. Throws with a friendly message
 * on unsupported codecs / corrupt files — the caller should surface it
 * as a toast rather than a stack trace.
 */
export async function decodeFile(file: File): Promise<AudioBuffer> {
  const buf = await file.arrayBuffer();
  try {
    // decodeAudioData mutates its input on some browsers — pass a copy.
    return await getCtx().decodeAudioData(buf.slice(0));
  } catch (err: any) {
    throw new Error(`Could not read this file — ${err?.message || 'unsupported format'}`);
  }
}

/**
 * Downsample an AudioBuffer to `barCount` peak values (0..1). Averaging
 * the max absolute value inside each bucket gives a shape that reads
 * like a "typical" DAW waveform. Uses channel 0 — for stereo tracks the
 * left channel is a close-enough proxy for a summary view.
 */
export function samplePeaks(buffer: AudioBuffer, barCount = 400): Float32Array {
  const data = buffer.getChannelData(0);
  const bucketSize = Math.max(1, Math.floor(data.length / barCount));
  const peaks = new Float32Array(barCount);
  for (let i = 0; i < barCount; i++) {
    let peak = 0;
    const start = i * bucketSize;
    const end = Math.min(data.length, start + bucketSize);
    for (let j = start; j < end; j++) {
      const v = Math.abs(data[j]);
      if (v > peak) peak = v;
    }
    peaks[i] = peak;
  }
  return peaks;
}

/**
 * Extract [startSec, startSec+durationSec) as a new AudioBuffer, resampled
 * to a target rate (defaults to whatever the source is). Handles stereo
 * → mono downmix along the way — Mureka accepts mono and it halves the
 * upload size.
 */
export function extractSlice(
  source: AudioBuffer,
  startSec: number,
  durationSec: number,
  targetSampleRate?: number,
): AudioBuffer {
  const sr = source.sampleRate;
  const outSr = targetSampleRate || sr;
  const startFrame = Math.max(0, Math.floor(startSec * sr));
  const endFrame = Math.min(source.length, startFrame + Math.floor(durationSec * sr));
  const inFrames = endFrame - startFrame;

  // Downmix to mono by averaging channels.
  const mono = new Float32Array(inFrames);
  const chCount = source.numberOfChannels;
  for (let ch = 0; ch < chCount; ch++) {
    const data = source.getChannelData(ch);
    for (let i = 0; i < inFrames; i++) mono[i] += data[startFrame + i] / chCount;
  }

  if (outSr === sr) {
    const out = getCtx().createBuffer(1, inFrames, sr);
    out.copyToChannel(mono, 0);
    return out;
  }

  // Linear resample. Sample-accurate enough for a reference-audio seed;
  // Mureka isn't listening for aliasing at this sample rate range.
  const outFrames = Math.round(inFrames * (outSr / sr));
  const out = getCtx().createBuffer(1, outFrames, outSr);
  const outData = out.getChannelData(0);
  const ratio = inFrames / outFrames;
  for (let i = 0; i < outFrames; i++) {
    const srcIdx = i * ratio;
    const i0 = Math.floor(srcIdx);
    const i1 = Math.min(inFrames - 1, i0 + 1);
    const t = srcIdx - i0;
    outData[i] = mono[i0] * (1 - t) + mono[i1] * t;
  }
  return out;
}

// lamejs is CommonJS UMD — Vite's interop can shape the module in a
// few different ways depending on version. Reach through the possible
// shells so a bad dev-vs-prod bundle doesn't silently return undefined.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as lamejsNs from 'lamejs';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Mp3EncoderCtor: any =
  (lamejsNs as any).Mp3Encoder ||
  (lamejsNs as any).default?.Mp3Encoder ||
  (lamejsNs as any).default;

/**
 * Encode an AudioBuffer to an MP3 Blob using lamejs (pure-JS LAME port).
 * Mureka rejects WAV with "File format not supported" (verified 2026-08-17
 * on JMJ-Oxigene reference upload) — MP3 is the only format we've seen
 * accepted from user uploads, matching what the iTunes preview path
 * already sends. ~940KB per 60s mono @ 44.1k / 128kbps.
 *
 * Throws with a legible message on any encoder issue so the caller can
 * surface it as a toast rather than silently failing under the "Use
 * this clip" button.
 */
export function encodeMp3(buffer: AudioBuffer, kbps = 128): Blob {
  if (typeof Mp3EncoderCtor !== 'function') {
    throw new Error('MP3 encoder unavailable — lamejs failed to load in this build');
  }

  const sr = buffer.sampleRate;
  const numChannels = 1; // extractSlice already downmixes to mono
  const encoder = new Mp3EncoderCtor(numChannels, sr, kbps);
  const data = buffer.getChannelData(0);
  const numFrames = data.length;

  // lamejs takes Int16 PCM. Convert in a single pass — reusing one
  // buffer for all frames keeps peak memory reasonable on mobile.
  const samples = new Int16Array(numFrames);
  for (let i = 0; i < numFrames; i++) {
    const s = Math.max(-1, Math.min(1, data[i]));
    samples[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }

  // Feed in MP3-frame-sized chunks (1152 samples = one MP3 frame).
  // Encoder buffers internally and returns bytes as full frames complete.
  const CHUNK = 1152;
  const mp3Chunks: Uint8Array[] = [];
  for (let i = 0; i < samples.length; i += CHUNK) {
    const chunk = samples.subarray(i, i + CHUNK);
    const buf = encoder.encodeBuffer(chunk);
    if (buf && buf.length > 0) mp3Chunks.push(new Uint8Array(buf));
  }
  const end = encoder.flush();
  if (end && end.length > 0) mp3Chunks.push(new Uint8Array(end));

  if (mp3Chunks.length === 0) {
    throw new Error('MP3 encoder produced no output — audio too short or all-silence');
  }

  // Cast: TS 5+ narrows Uint8Array<ArrayBufferLike> but BlobPart wants
  // ArrayBuffer/BufferSource — same bytes at runtime, difference is
  // structural typing over the underlying storage class.
  return new Blob(mp3Chunks as unknown as BlobPart[], { type: 'audio/mpeg' });
}

/**
 * Encode an AudioBuffer to an uncompressed 16-bit PCM WAV Blob. Kept for
 * potential fallback use but NOT the default — Mureka rejects it. WAV is
 * verbose (~5MB per 60s mono @ 44.1k) but decodable by any tool.
 */
export function encodeWav(buffer: AudioBuffer): Blob {
  const sr = buffer.sampleRate;
  const numChannels = 1; // we always downmix in extractSlice
  const data = buffer.getChannelData(0);
  const numFrames = data.length;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sr * blockAlign;
  const dataSize = numFrames * blockAlign;
  const bufferSize = 44 + dataSize;
  const ab = new ArrayBuffer(bufferSize);
  const view = new DataView(ab);

  // RIFF header
  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeAscii(view, 8, 'WAVE');
  // fmt chunk
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);           // subchunk size
  view.setUint16(20, 1, true);            // PCM = 1
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sr, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);           // bits per sample
  // data chunk
  writeAscii(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  // PCM samples — clamp + convert float32 to int16.
  let offset = 44;
  for (let i = 0; i < numFrames; i++) {
    const s = Math.max(-1, Math.min(1, data[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    offset += 2;
  }

  return new Blob([ab], { type: 'audio/wav' });
}

function writeAscii(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
}

/**
 * Play a slice of the source buffer through the AudioContext. Returns
 * the running node so the caller can stop it early. Auto-resumes the
 * context if it's been suspended by an autoplay policy.
 */
export function previewSlice(
  source: AudioBuffer,
  startSec: number,
  durationSec: number,
): AudioBufferSourceNode {
  const ctx = getCtx();
  if (ctx.state === 'suspended') ctx.resume().catch(() => undefined);
  const buf = extractSlice(source, startSec, durationSec);
  const node = ctx.createBufferSource();
  node.buffer = buf;
  node.connect(ctx.destination);
  node.start(0);
  return node;
}
